import React, { useState, useEffect } from "react";
import { BASE_URL } from "utils/constant";
import CalendarPicker from "./CalenderPicker";
import { useAlert } from "components/UI_components/Alert";

const DEFAULT_PRIORITY = 3;

const toDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const parts = String(value).split("-");
  if (parts.length === 3) {
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m - 1, d);
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateForApi = (value) => {
  const date = toDateValue(value);
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const AddSubtaskModal = ({
  handleSubmit,
  onClose,
  taskId,
}) => {
  const { addAlert } = useAlert();
  const [subtaskName, setSubtaskName] = useState("");
  const [subtaskDetails, setSubtaskDetails] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [taskMembers, setTaskMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem("access");
  const authHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    fetchTaskMembers();
  }, [taskId]);

  const fetchTaskMembers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/task/get-task-members/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ task_id: taskId }),
      });

      const result = await response.json();
      if (result.status && result.data) {
        setTaskMembers(result.data);
      } else {
        console.error("Failed to fetch task members:", result.message);
      }
    } catch (error) {
      console.error("Error fetching task members:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (memberId) => {
    setSelectedMembers((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const subtaskNameValue = subtaskName.trim();
    if (!subtaskNameValue) {
      addAlert("Subtask name cannot be empty.", "warning");
      return;
    }

    const selectedStartDate = startDate || endDate;
    const selectedEndDate = endDate || startDate;
    const startISO = formatDateForApi(selectedStartDate);
    const endISO = formatDateForApi(selectedEndDate);

    if (!startISO || !endISO) {
      addAlert("Please select a valid date range.", "warning");
      return;
    }

    const selectedStart = toDateValue(startISO);
    const selectedEnd = toDateValue(endISO);

    if (selectedStart && selectedEnd && selectedStart > selectedEnd) {
      addAlert("Start date cannot be after end date.", "warning");
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await handleSubmit({
        task_id: taskId,
        subtask_name: subtaskNameValue,
        subtask_details: subtaskDetails.trim(),
        start_date: startISO,
        end_date: endISO,
        priority: DEFAULT_PRIORITY,
        assign_to: selectedMembers,
      });

      if (result === false || result === null) {
        return;
      }

      onClose();
    } catch (error) {
      addAlert(error?.message || "Could not create subtask.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50 p-3">
      <div
        className="mx-2 w-full max-w-lg rounded-lg bg-white shadow-xl"
        style={{ maxHeight: "80vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative pt-3 pb-4">

          <div className="mb-3 flex px-4 pb-2 border-b flex-row items-center justify-between">
            <h2 className="text-xl font-bold">Add Subtask</h2>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close Add Subtask modal"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleFormSubmit} noValidate className="space-y-3 px-4">
            <div>
              <label className="mb-1 block text-sm font-medium">
                Subtask Name
              </label>
              <input
                type="text"
                value={subtaskName}
                onChange={(e) => setSubtaskName(e.target.value)}
                className="w-full rounded border p-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Details</label>
              <textarea
                value={subtaskDetails}
                onChange={(e) => setSubtaskDetails(e.target.value)}
                className="w-full rounded border p-2"
                rows="2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Date Range
              </label>
              <div className="w-full rounded border px-2 py-2">
                <CalendarPicker
                  startDate={startDate}
                  endDate={endDate}
                  onDateChange={(newStartDate, newEndDate) => {
                    setStartDate(newStartDate || null);
                    setEndDate(newEndDate || null);
                  }}
                  placeholder="Select Date"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Assign To
              </label>
              {loading ? (
                <div className="rounded border p-2 text-sm text-gray-500">
                  Loading members...
                </div>
              ) : (
                <div className="max-h-32 overflow-y-auto rounded border p-1.5">
                  {taskMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex cursor-pointer items-center rounded px-2 py-1.5 hover:bg-gray-100"
                      onClick={() => handleMemberSelect(member.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.id)}
                        onChange={() => { }}
                        className="mr-2"
                      />
                      <span>
                        {member.first_name} {member.last_name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded bg-sm-primary px-3 py-1.5 text-white hover:bg-sm-primary/80"
              >
                {isSubmitting ? "Adding..." : "Add Subtask"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSubtaskModal;
